import React, { useState, useRef } from 'react';
import {
  FileText,
  Paintbrush,
  Layout,
  Smartphone,
  Sliders,
  X,
  ChevronUp,
  ChevronDown,
  Trash2,
  Copy,
  ArrowUp,
  ArrowDown,
  Check,
  Settings,
  Sparkles,
} from 'lucide-react';
import { useSite } from '../../context/SiteContext';
import { ContentTab } from './properties/ContentTab';
import { DesignTab } from './properties/DesignTab';
import { LayoutTab } from './properties/LayoutTab';
import { ResponsiveInspector } from './properties/ResponsiveInspector';
import { AnimationTab } from './properties/AnimationTab';
import { GlobalSettingsInspector } from './properties/GlobalSettingsInspector';
import { AnimationConfig } from '../../types';

type EditorTab = 'content' | 'design' | 'animation' | 'layout' | 'responsive';
type SheetSnap = 'compact' | 'half' | 'full';

interface MobilePropertySheetProps {
  isOpen: boolean;
  onClose: () => void;
}

export const MobilePropertySheet: React.FC<MobilePropertySheetProps> = ({ isOpen, onClose }) => {
  const {
    activeSite,
    activePageId,
    selectedSectionId,
    setSelectedSectionId,
    updateSectionInActivePage,
    deleteSectionInActivePage,
    duplicateSectionInActivePage,
    moveSectionInActivePage,
    autosaveStatus,
  } = useSite();

  const [activeTab, setActiveTab] = useState<EditorTab>('content');
  const [snapHeight, setSnapHeight] = useState<SheetSnap>('half');

  const sheetRef = useRef<HTMLDivElement>(null);
  const touchStartY = useRef<number>(0);

  if (!isOpen || !activeSite) return null;

  const activePage = activeSite.pages?.find((p) => p && p.id === activePageId) || activeSite.pages?.[0];
  const sections = activePage?.sections || [];
  const selectedSection = sections.find((s) => s.id === selectedSectionId);
  const selectedIndex = sections.findIndex((s) => s.id === selectedSectionId);

  const sectionTypeLabels: Record<string, string> = {
    pricing: 'Fiyatlandırma & Paketler',
    PRICING: 'Fiyatlandırma & Paketler',
    hero: 'Giriş & Manşet (Hero)',
    HERO: 'Giriş & Manşet (Hero)',
    services: 'Hizmetler & Özellikler',
    SERVICES: 'Hizmetler & Özellikler',
    about: 'Hakkımızda & Hikaye',
    ABOUT: 'Hakkımızda & Hikaye',
    contact: 'İletişim & Form',
    CONTACT: 'İletişim & Form',
    yacht_fleet: 'Yat & Tekne Filosu',
    YACHT_FLEET: 'Yat & Tekne Filosu',
    faq: 'Sıkça Sorulan Sorular',
    FAQ: 'Sıkça Sorulan Sorular',
    testimonials: 'Müşteri Yorumları',
    TESTIMONIALS: 'Müşteri Yorumları',
    gallery: 'Fotoğraf Galerisi',
    GALLERY: 'Fotoğraf Galerisi',
    stats: 'İstatistik & Rakamlar',
    STATS: 'İstatistik & Rakamlar',
    team: 'Ekip & Kadro',
    TEAM: 'Ekip & Kadro',
    references: 'Referans Logoları',
    REFERENCES: 'Referans Logoları',
    reservation_cta: 'Rezervasyon Çağrısı',
    RESERVATION_CTA: 'Rezervasyon Çağrısı',
    header: 'Üst Menü (Header)',
    HEADER: 'Üst Menü (Header)',
    footer: 'Alt Bilgi (Footer)',
    FOOTER: 'Alt Bilgi (Footer)',
  };

  const currentLabel = selectedSection
    ? sectionTypeLabels[selectedSection.type] || selectedSection.name || 'Özel Bölüm'
    : 'Genel Site Ayarları';

  const handleUpdateContent = (updates: any) => {
    if (!selectedSection) return;
    updateSectionInActivePage(selectedSection.id, {
      ...selectedSection.content,
      ...updates,
    });
  };

  const handleUpdateStyle = (updates: any) => {
    if (!selectedSection) return;
    updateSectionInActivePage(
      selectedSection.id,
      selectedSection.content,
      {
        ...selectedSection.style,
        ...updates,
      }
    );
  };

  const handleDuplicate = () => {
    if (!selectedSection) return;
    duplicateSectionInActivePage(selectedSection.id);
  };

  const handleDelete = () => {
    if (!selectedSection) return;
    deleteSectionInActivePage(selectedSection.id);
    setSelectedSectionId(null);
    onClose();
  };

  const handleMoveUp = () => {
    if (selectedIndex <= 0) return;
    moveSectionInActivePage(selectedIndex, selectedIndex - 1);
  };

  const handleMoveDown = () => {
    if (selectedIndex < 0 || selectedIndex >= sections.length - 1) return;
    moveSectionInActivePage(selectedIndex, selectedIndex + 1);
  };

  const handleUpdateAnimation = (newAnimation: AnimationConfig) => {
    updateSectionInActivePage(
      selectedSection.id,
      selectedSection.content,
      {
        ...selectedSection.style,
        animation: newAnimation,
      }
    );
  };

  const handlePreviewAnimation = () => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('adiosoft-preview-anim', {
          detail: { sectionId: selectedSection.id },
        })
      );
    }
  };

  // Touch gesture handlers for dragging sheet height
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const deltaY = e.changedTouches[0].clientY - touchStartY.current;
    if (deltaY > 80) {
      // Swiped down
      if (snapHeight === 'full') setSnapHeight('half');
      else if (snapHeight === 'half') setSnapHeight('compact');
      else onClose();
    } else if (deltaY < -60) {
      // Swiped up
      if (snapHeight === 'compact') setSnapHeight('half');
      else if (snapHeight === 'half') setSnapHeight('full');
    }
  };

  const snapClasses = {
    compact: 'h-[38dvh]',
    half: 'h-[62dvh]',
    full: 'h-[92dvh]',
  };

  const tabs: Array<{ id: EditorTab; label: string; icon: React.ComponentType<{ className?: string }> }> = [
    { id: 'content', label: 'İçerik', icon: FileText },
    { id: 'design', label: 'Tasarım', icon: Paintbrush },
    { id: 'animation', label: 'Animasyon', icon: Sparkles },
    { id: 'layout', label: 'Düzen', icon: Layout },
    { id: 'responsive', label: 'Mobil Uyum', icon: Smartphone },
  ];

  return (
    <div className="fixed inset-0 z-50 lg:hidden flex flex-col justify-end">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs transition-opacity"
        onClick={onClose}
      />

      {/* Sheet Container */}
      <div
        ref={sheetRef}
        className={`relative z-10 w-full bg-slate-900 border-t border-slate-700/80 rounded-t-3xl shadow-2xl flex flex-col transition-all duration-300 ease-out select-none text-slate-100 ${snapClasses[snapHeight]}`}
      >
        {/* Drag Handle & Top Controls */}
        <div
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          className="pt-2.5 pb-2 px-4 flex flex-col items-center bg-slate-950/60 border-b border-slate-800 shrink-0 cursor-grab active:cursor-grabbing"
        >
          {/* Pill handle */}
          <div className="w-12 h-1.5 rounded-full bg-slate-600 hover:bg-slate-500 mb-2 transition" />

          {/* Title Row */}
          <div className="w-full flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-7 h-7 rounded-lg bg-blue-600/20 border border-blue-500/30 text-blue-400 flex items-center justify-center shrink-0">
                {selectedSection ? <Sliders className="w-4 h-4" /> : <Settings className="w-4 h-4" />}
              </div>
              <div className="min-w-0">
                <h3 className="text-xs font-black text-white truncate">{currentLabel}</h3>
                {selectedSection && (
                  <span className="text-[10px] text-slate-400 block font-mono">
                    Sıra: {selectedIndex + 1} / {sections.length}
                  </span>
                )}
              </div>
            </div>

            {/* Quick Actions (Move Up/Down, Duplicate, Delete, Height toggle, Close) */}
            <div className="flex items-center gap-1 shrink-0">
              {selectedSection && (
                <>
                  <button
                    disabled={selectedIndex <= 0}
                    onClick={handleMoveUp}
                    className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 disabled:opacity-30 transition"
                    title="Yukarı Taşı"
                  >
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button
                    disabled={selectedIndex >= sections.length - 1}
                    onClick={handleMoveDown}
                    className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 disabled:opacity-30 transition"
                    title="Aşağı Taşı"
                  >
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={handleDuplicate}
                    className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 transition"
                    title="Kopyala"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={handleDelete}
                    className="p-1.5 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/20 transition"
                    title="Sil"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </>
              )}

              {/* Snap Height Toggle */}
              <button
                onClick={() => {
                  if (snapHeight === 'compact') setSnapHeight('half');
                  else if (snapHeight === 'half') setSnapHeight('full');
                  else setSnapHeight('compact');
                }}
                className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 transition"
                title="Yüksekliği Ayarla"
              >
                {snapHeight === 'full' ? (
                  <ChevronDown className="w-4 h-4 text-blue-400" />
                ) : (
                  <ChevronUp className="w-4 h-4 text-blue-400" />
                )}
              </button>

              {/* Close Sheet */}
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Tab Navigation (Only if section selected) */}
        {selectedSection && (
          <div className="flex items-center border-b border-slate-800 bg-slate-950/40 px-2 shrink-0 overflow-x-auto custom-scrollbar">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 min-w-[70px] py-2.5 px-2 text-[11px] font-bold flex items-center justify-center gap-1.5 border-b-2 transition whitespace-nowrap ${
                    isActive
                      ? 'border-blue-500 text-blue-400 bg-blue-500/5'
                      : 'border-transparent text-slate-400 hover:text-slate-200'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Scrollable Content Body */}
        <div className="flex-1 overflow-y-auto p-4 custom-scrollbar space-y-4">
          {!selectedSection ? (
            <GlobalSettingsInspector />
          ) : (
            <>
              {activeTab === 'content' && (
                <ContentTab
                  section={selectedSection}
                  onUpdateContent={handleUpdateContent}
                />
              )}

              {activeTab === 'design' && (
                <DesignTab
                  section={selectedSection}
                  onUpdateStyle={handleUpdateStyle}
                />
              )}

              {activeTab === 'animation' && (
                <AnimationTab
                  section={selectedSection}
                  allPageSections={sections}
                  onUpdateAnimation={handleUpdateAnimation}
                  onPreviewAnimation={handlePreviewAnimation}
                />
              )}

              {activeTab === 'layout' && (
                <LayoutTab
                  section={selectedSection}
                  onUpdateStyle={handleUpdateStyle}
                />
              )}

              {activeTab === 'responsive' && (
                <ResponsiveInspector
                  section={selectedSection}
                  onUpdateStyle={handleUpdateStyle}
                />
              )}
            </>
          )}
        </div>

        {/* Sticky Mobile Action Footer */}
        <div className="p-3 bg-slate-950 border-t border-slate-800 flex items-center justify-between gap-3 shrink-0 pb-safe">
          <div className="text-[11px] text-slate-400 flex items-center gap-1.5">
            <span
              className={`w-2 h-2 rounded-full ${
                autosaveStatus === 'SAVED'
                  ? 'bg-emerald-400'
                  : autosaveStatus === 'SYNCING'
                  ? 'bg-amber-400 animate-pulse'
                  : 'bg-slate-500'
              }`}
            />
            <span>
              {autosaveStatus === 'SAVED'
                ? 'Otomatik Kaydedildi'
                : autosaveStatus === 'SYNCING'
                ? 'Kaydediliyor...'
                : 'Canlı Önizleme'}
            </span>
          </div>

          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs shadow-md flex items-center gap-1.5 transition active:scale-95"
          >
            <Check className="w-3.5 h-3.5" />
            <span>Kapat & İncele</span>
          </button>
        </div>
      </div>
    </div>
  );
};
